import { iconSizes } from '@mds/tokens';
import {
  ListItem,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  listItemIconClasses,
  ListItemProps,
  ListItemText,
  svgIconClasses,
} from '@mui/material';
import { ReactNode } from 'react';

export type AccountMenuItemButtonProps = { icon: ReactNode; endIcon?: ReactNode } & ListItemButtonProps;

/**
 * Use this component to define a custom button for the account menu.
 *
 * Note that clicking on items within the drawer and account menu will close it. To opt
 * out of this behaviour, use `onClick={event => event.stopPropagation()}`.
 */
export function AccountMenuItemButton<ComponentT extends React.ElementType>({
  icon,
  endIcon,
  children,
  sx,
  ...rest
}: { icon: ReactNode; endIcon?: ReactNode } & ListItemButtonProps<ComponentT, { component?: ComponentT }>) {
  return (
    <ListItemButton
      divider={false}
      {...rest}
      sx={[
        { flex: 0, [`.${listItemIconClasses.root} .${svgIconClasses.root}`]: { fontSize: iconSizes.md } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {/* noWrap - this button can render inside a container that's still narrower than its
          final width mid-transition (e.g. SideNav animating rail <-> open width). Without it,
          long enough primary text wraps onto a second line for that instant, then snaps back
          to one line once the transition catches up - noWrap clips it instead so it always
          reads as one line. */}
      <ListItemText primary={children} slotProps={{ primary: { noWrap: true } }} />
      {endIcon && <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary', flexShrink: 0 }}>{endIcon}</ListItemIcon>}
    </ListItemButton>
  );
}

export type AccountMenuItemProps = { icon?: ReactNode } & ListItemButtonProps;

/**
 * Use this component to define a custom list item for the account menu.
 *
 * Note that clicking on items within the drawer and account menu will close it. To opt
 * out of this behaviour, use `onClick={event => event.stopPropagation()}`.
 */
export function AccountMenuItem<ComponentT extends React.ElementType>({
  icon,
  children,
  sx,
  ...rest
}: { icon?: ReactNode } & ListItemProps<ComponentT, { component?: ComponentT }>) {
  return (
    <ListItem
      component="div"
      divider={false}
      {...rest}
      sx={[
        { flex: 0, [`.${listItemIconClasses.root} .${svgIconClasses.root}`]: { fontSize: iconSizes.md } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </ListItem>
  );
}
